import { useState, useEffect, useRef } from "react";
import { Form, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { crearDisentimiento } from "../api/consentimientoAPI";
import {
  useOnlineStatus,
  guardarDisentimientoOffline,
  iniciarSincronizacionAutomaticaDisentimiento,
} from "../hooks/useConsentimientoSync";

// Municipios y áreas no municipalizadas del Amazonas. Si cambia la lista,
// se edita acá (y en la constante igual del panel admin).
const MUNICIPIOS = [
  "Leticia", "Puerto Nariño",
  "El Encanto", "La Chorrera", "La Pedrera", "La Victoria", "Mirití-Paraná",
  "Puerto Alegría", "Puerto Arica", "Puerto Santander", "Tarapacá",
];
const VINCULOS = ["Madre", "Padre", "Representante legal", "Otro"];

// Quien vacuna suele registrar varias constancias seguidas desde el mismo
// punto — se recuerdan municipio y profesional para no reescribirlos cada vez.
const CLAVE_ULTIMO_PUNTO = "disentimiento_ultimo_punto_atencion";
const leerUltimoPunto = () => {
  try { return JSON.parse(localStorage.getItem(CLAVE_ULTIMO_PUNTO)) || {}; } catch { return {}; }
};
const guardarUltimoPunto = (datos) => {
  try { localStorage.setItem(CLAVE_ULTIMO_PUNTO, JSON.stringify(datos)); } catch { /* no crítico */ }
};

const pad = (n) => String(n).padStart(2, "0");
// Fecha/hora LOCAL — no toISOString(), que es UTC y en Colombia (UTC-5)
// ya marca "mañana" después de las 7 p. m.
const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const horaLocalAhora = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formularioInicial = () => {
  const ultimo = leerUltimoPunto();
  return {
    municipio: ultimo.municipio || "",
    fecha_atencion: fechaLocalHoy(),
    hora_atencion: horaLocalAhora(),
    menor_nombre: "", menor_documento: "", menor_edad: "", menor_edad_unidad: "Años",
    representante_nombre: "", representante_documento: "",
    representante_vinculo: "", representante_vinculo_otro: "",
    representante_telefono: "", representante_correo: "",
    vacunas_ofrecidas: "", motivo_decision: "",
    profesional_nombre: ultimo.profesional_nombre || "", profesional_cargo: ultimo.profesional_cargo || "",
    no_firma: false, se_ofrecio_lectura_firma: "", testigo_nombre: "",
  };
};

function generarCodigoDisentimiento() {
  const now = new Date();
  const fecha = pad(now.getDate()) + pad(now.getMonth() + 1) + now.getFullYear();
  const hora = pad(now.getHours()) + pad(now.getMinutes()) + pad(now.getSeconds()) + String(now.getMilliseconds()).padStart(3, "0");
  return `DIS-${fecha}-${hora}`;
}

export default function ConsentimientoPaiHome() {
  const [f, setF] = useState(formularioInicial);
  const set = (campo, valor) => setF(p => ({ ...p, [campo]: valor }));

  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(null);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const firmandoRef = useRef(false);
  const [hayFirma, setHayFirma] = useState(false);

  const online = useOnlineStatus();

  useEffect(() => {
    // Arranca la sincronización automática de pendientes — se dispara
    // sola al cargar (si ya hay señal) y cada vez que vuelva.
    iniciarSincronizacionAutomaticaDisentimiento();
  }, []);

  // El canvas se (re)inicializa cada vez que el formulario vuelve a
  // mostrarse. Antes esto corría solo una vez al montar: al enviar, la
  // pantalla de éxito desmonta el canvas, y al "Registrar otro" nacía un
  // canvas nuevo con el contexto del viejo (ya desmontado) — la firma se
  // dibujaba en el vacío y se enviaba en blanco a partir del 2.º registro.
  useEffect(() => {
    if (enviado) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#16302E";
    ctxRef.current = ctx;
  }, [enviado]);

  const posicion = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const empezarFirma = (e) => {
    e.preventDefault();
    firmandoRef.current = true;
    setHayFirma(true);
    const p = posicion(e);
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(p.x, p.y);
  };
  const moverFirma = (e) => {
    if (!firmandoRef.current) return;
    e.preventDefault();
    const p = posicion(e);
    ctxRef.current.lineTo(p.x, p.y);
    ctxRef.current.stroke();
  };
  const terminarFirma = () => { firmandoRef.current = false; };
  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    if (canvas && ctxRef.current) {
      ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHayFirma(false);
  };

  // Si quien representa al menor no firma, se descarta cualquier trazo
  // que hubiera quedado, para no enviar una firma a medias por error.
  const alternarNoFirma = (valor) => {
    if (valor) limpiarFirma();
    setF(p => ({ ...p, no_firma: valor, se_ofrecio_lectura_firma: "", testigo_nombre: "" }));
  };

  const validar = () => {
    if (!f.municipio) return "Selecciona el municipio";
    if (!f.fecha_atencion) return "Falta la fecha de atención";
    if (!f.menor_nombre.trim()) return "Falta el nombre del niño, niña o adolescente";
    if (!f.representante_nombre.trim() || !f.representante_documento.trim()) return "Completa nombre y documento de la madre, padre o representante";
    if (!f.representante_vinculo) return "Selecciona el vínculo o calidad de representación";
    if (f.representante_vinculo === "Otro" && !f.representante_vinculo_otro.trim()) return "Especifica el vínculo";
    if (f.representante_correo.trim() && !/^\S+@\S+\.\S+$/.test(f.representante_correo.trim())) return "El correo no parece válido";
    if (!f.vacunas_ofrecidas.trim()) return "Anota la(s) vacuna(s) y dosis indicada(s) en esta visita";
    if (!f.motivo_decision.trim()) return "Falta el motivo de la decisión";
    if (!f.profesional_nombre.trim()) return "Falta el nombre del profesional o vacunador que informó";
    if (f.no_firma) {
      if (!f.se_ofrecio_lectura_firma) return "Indica si se ofreció lectura y firma del formato";
      if (!f.testigo_nombre.trim()) return "Falta el nombre del testigo";
    } else if (!hayFirma) {
      return "Falta la firma";
    }
    return null;
  };

  const enviar = async () => {
    const error = validar();
    if (error) {
      Swal.fire({ icon: "warning", title: error, timer: 2400, showConfirmButton: false });
      return;
    }

    setEnviando(true);
    try {
      const vinculo = f.representante_vinculo === "Otro" ? f.representante_vinculo_otro.trim() : f.representante_vinculo;
      const tieneEdad = f.menor_edad !== "";
      const payload = {
        codigo: generarCodigoDisentimiento(),
        municipio: f.municipio,
        fecha_atencion: f.fecha_atencion,
        hora_atencion: f.hora_atencion || null,
        menor_nombre: f.menor_nombre.trim(),
        menor_documento: f.menor_documento.trim() || null,
        menor_edad: tieneEdad ? Number(f.menor_edad) : null,
        menor_edad_unidad: tieneEdad ? f.menor_edad_unidad : null,
        representante_nombre: f.representante_nombre.trim(),
        representante_documento: f.representante_documento.trim(),
        representante_vinculo: vinculo,
        representante_telefono: f.representante_telefono.trim() || null,
        representante_correo: f.representante_correo.trim() || null,
        vacunas_ofrecidas: f.vacunas_ofrecidas.trim(),
        motivo_decision: f.motivo_decision.trim(),
        firma_base64: f.no_firma ? null : canvasRef.current.toDataURL("image/png"),
        profesional_nombre: f.profesional_nombre.trim(),
        profesional_cargo: f.profesional_cargo.trim() || null,
        se_ofrecio_lectura_firma: f.no_firma ? f.se_ofrecio_lectura_firma : null,
        testigo_nombre: f.no_firma ? f.testigo_nombre.trim() : null,
      };

      // Se recuerdan antes de enviar, para que queden aunque el envío falle.
      guardarUltimoPunto({
        municipio: f.municipio,
        profesional_nombre: f.profesional_nombre.trim(),
        profesional_cargo: f.profesional_cargo.trim(),
      });

      let quedoOffline = false;
      if (!online) {
        await guardarDisentimientoOffline(payload);
        quedoOffline = true;
      } else {
        try {
          await crearDisentimiento(payload);
        } catch (errEnLinea) {
          // Si el servidor SÍ respondió con un error de datos (4xx), es un
          // problema real del formulario — no tiene caso guardarlo offline,
          // hay que corregirlo y reintentar. Si NO hubo respuesta (sin
          // señal, tiempo agotado), ahí sí se guarda local como respaldo,
          // para no perder lo que ya se llenó.
          if (errEnLinea.response) throw errEnLinea;
          console.warn("⚠️ No se pudo enviar en línea, se guarda offline como respaldo:", errEnLinea.message);
          await guardarDisentimientoOffline(payload);
          quedoOffline = true;
        }
      }

      setEnviado({
        menor: f.menor_nombre.trim(),
        representante: f.representante_nombre.trim(),
        municipio: f.municipio,
        correo: f.representante_correo.trim(),
        offline: quedoOffline,
      });
    } catch (err) {
      Swal.fire("No se pudo enviar", err.response?.data?.message || "Intenta de nuevo en un momento.", "error");
    } finally {
      setEnviando(false);
    }
  };

  const reiniciar = () => {
    setF(formularioInicial());
    setHayFirma(false);
    setEnviado(null);
  };

  if (enviado) {
    return (
      <div style={{ minHeight: "100vh", background: "#FBF9F4" }}>
        <div style={{ maxWidth: 420, margin: "0 auto", padding: "60px 22px", textAlign: "center" }}>
          <div style={{ width: 68, height: 68, borderRadius: "50%", background: enviado.offline ? "#FCE7DA" : "#E5F3E3", color: enviado.offline ? "#CC5A26" : "#2C6B2F", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>{enviado.offline ? "☁️" : "✓"}</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#16302E", marginBottom: 10 }}>{enviado.offline ? "Constancia guardada en el dispositivo" : "Constancia registrada"}</h2>
          <p style={{ fontSize: 14, color: "#4C6360", marginBottom: 26, lineHeight: 1.6 }}>
            {enviado.offline
              ? "No había conexión en este momento — se guardó en el dispositivo y se enviará sola apenas haya señal. No hace falta hacer nada más."
              : enviado.correo
                ? `La ficha se está generando y se enviará por correo a ${enviado.correo}.`
                : "La constancia quedó registrada. No se envió copia por correo porque no se dejó una dirección; el personal de salud puede descargar la ficha desde el panel."}
          </p>
          <div style={{ textAlign: "center", background: "#fff", border: "1px solid #DDE5E0", borderRadius: 14, padding: "18px 20px", fontSize: 13 }}>
            <FilaResumen label="Niño, niña o adolescente" valor={enviado.menor} />
            <FilaResumen label="Representante" valor={enviado.representante} />
            <FilaResumen label="Municipio" valor={enviado.municipio} ultima />
          </div>
          <div style={{ marginTop: 22 }}>
            <span onClick={reiniciar} style={{ fontSize: 13, fontWeight: 700, color: "#2BA8C4", cursor: "pointer" }}>← Registrar otra constancia</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-consentimiento" style={{ minHeight: "100vh", background: "#FBF9F4" }}>
      <style>{`
        .form-consentimiento .form-control,
        .form-consentimiento .form-select {
          border-radius: 12px; border: 1.5px solid #DDE5E0; padding: 11px 14px; font-size: 15px;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .form-consentimiento .form-control:focus,
        .form-consentimiento .form-select:focus {
          border-color: #2BA8C4; box-shadow: 0 0 0 3px rgba(43,168,196,0.15);
        }
        .form-consentimiento .form-control::placeholder { color: #A9B6B1; }
      `}</style>
      <div style={{ background: "linear-gradient(160deg, #0E4F57, #146873)", color: "#fff", padding: "32px 20px 30px", position: "relative", overflow: "hidden" }}>
        <svg viewBox="0 0 100 100" fill="#fff" style={{ position: "absolute", top: -28, right: -28, width: 170, opacity: .14 }}>
          <path d="M50 5C30 15 15 35 15 55c0 20 15 35 35 40 5-25-5-50-25-65C40 25 55 10 50 5z" />
        </svg>
        <svg viewBox="0 0 100 100" fill="#fff" style={{ position: "absolute", bottom: -40, left: -34, width: 130, opacity: .12, transform: "rotate(35deg)" }}>
          <path d="M50 5C30 15 15 35 15 55c0 20 15 35 35 40 5-25-5-50-25-65C40 25 55 10 50 5z" />
        </svg>
        <div style={{ maxWidth: 440, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".01em", opacity: .85, marginBottom: 10 }}>Secretaría de Salud Departamental · PAI</p>
          <h1 style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.22, marginBottom: 12 }}>Constancia de disentimiento informado frente a la vacunación</h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, opacity: .92, margin: "0 auto", maxWidth: 380 }}>
            Para registrar la decisión de la madre, el padre o representante legal cuando, después de recibir información, no acepta la vacunación indicada en esta oportunidad.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 480, margin: "24px auto 60px", padding: "0 20px" }}>

        <Seccion numero={1} titulo="Datos de atención">
          <Campo label="Municipio">
            <Form.Select value={f.municipio} onChange={e => set("municipio", e.target.value)}>
              <option value="">Selecciona el municipio...</option>
              {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
            </Form.Select>
          </Campo>
          <div className="row g-2">
            <div className="col-7">
              <Campo label="Fecha">
                <Form.Control type="date" value={f.fecha_atencion} max={fechaLocalHoy()} onChange={e => set("fecha_atencion", e.target.value)} />
              </Campo>
            </div>
            <div className="col-5">
              <Campo label="Hora">
                <Form.Control type="time" value={f.hora_atencion} onChange={e => set("hora_atencion", e.target.value)} />
              </Campo>
            </div>
          </div>
        </Seccion>

        <Seccion numero={2} titulo="Niño, niña o adolescente">
          <Campo label="Nombre completo">
            <Form.Control value={f.menor_nombre} onChange={e => set("menor_nombre", e.target.value)} placeholder="Nombre del niño, niña o adolescente" />
          </Campo>
          <Campo label="Documento de identidad (si tiene)">
            <Form.Control value={f.menor_documento} onChange={e => set("menor_documento", e.target.value)} placeholder="N.º documento" />
          </Campo>
          <div className="row g-2">
            <div className="col-6">
              <Campo label="Edad">
                <Form.Control type="number" min={0} max={f.menor_edad_unidad === "Meses" ? 36 : 17} value={f.menor_edad} onChange={e => set("menor_edad", e.target.value)} placeholder="0" />
              </Campo>
            </div>
            <div className="col-6">
              <Campo label="Unidad">
                <Form.Select value={f.menor_edad_unidad} onChange={e => set("menor_edad_unidad", e.target.value)}>
                  <option value="Años">Años</option>
                  <option value="Meses">Meses</option>
                </Form.Select>
              </Campo>
            </div>
          </div>
        </Seccion>

        <Seccion numero={3} titulo="Madre, padre o representante legal">
          <Campo label="Nombre completo">
            <Form.Control value={f.representante_nombre} onChange={e => set("representante_nombre", e.target.value)} placeholder="Nombre y apellidos" />
          </Campo>
          <Campo label="Documento de identidad">
            <Form.Control value={f.representante_documento} onChange={e => set("representante_documento", e.target.value)} placeholder="N.º documento" />
          </Campo>
          <Campo label="Vínculo o calidad de representación">
            <Form.Select value={f.representante_vinculo} onChange={e => set("representante_vinculo", e.target.value)}>
              <option value="">Selecciona...</option>
              {VINCULOS.map(v => <option key={v} value={v}>{v}</option>)}
            </Form.Select>
          </Campo>
          {f.representante_vinculo === "Otro" && (
            <Campo label="¿Cuál?">
              <Form.Control value={f.representante_vinculo_otro} onChange={e => set("representante_vinculo_otro", e.target.value)} placeholder="Ej: abuela con custodia" />
            </Campo>
          )}
          <Campo label="Teléfono de contacto">
            <Form.Control type="tel" value={f.representante_telefono} onChange={e => set("representante_telefono", e.target.value)} placeholder="300 000 0000" />
          </Campo>
          <Campo label="Correo electrónico (opcional)">
            <Form.Control type="email" value={f.representante_correo} onChange={e => set("representante_correo", e.target.value)} placeholder="correo@ejemplo.com" />
            <p style={{ fontSize: 11.5, color: "#83958F", margin: "6px 2px 0", lineHeight: 1.5 }}>
              Si lo deja, se le envía una copia de la constancia. Si no, la constancia se registra igual.
            </p>
          </Campo>
        </Seccion>

        <Seccion numero={4} titulo="Vacunación ofrecida e información brindada">
          <Campo label="Vacuna(s) y dosis indicada(s) en esta visita">
            <Form.Control as="textarea" rows={3} value={f.vacunas_ofrecidas} onChange={e => set("vacunas_ofrecidas", e.target.value)} placeholder="Ej: Pentavalente, 2.ª dosis" />
          </Campo>
          <div style={{ fontSize: 12.5, lineHeight: 1.65, color: "#4C6360", background: "#E4F5F8", borderRadius: 12, padding: 16, marginTop: 14, textAlign: "center" }}>
            Yo, quien firma, declaro que el personal de salud revisó los antecedentes disponibles y me explicó, en lenguaje comprensible, para qué sirve la vacunación indicada, sus beneficios, posibles reacciones y cuidados posteriores, así como los riesgos de no vacunar en esta oportunidad. Pude hacer preguntas y recibí respuesta. Se me informó que puedo solicitar nuevamente la vacunación.
          </div>
        </Seccion>

        <Seccion numero={5} titulo="Decisión">
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#16302E", fontWeight: 700, background: "#FCE7DA", borderRadius: 12, padding: 16, textAlign: "center" }}>
            Después de recibir la información, NO acepto que se aplique(n) a mi hijo(a) o representado(a) la(s) vacuna(s) anotada(s) arriba en esta oportunidad.
          </div>
          <Campo label="Motivo de mi decisión">
            <Form.Control as="textarea" rows={4} value={f.motivo_decision} onChange={e => set("motivo_decision", e.target.value)} placeholder="Escriba aquí el motivo" />
          </Campo>
          <p style={{ fontSize: 12, lineHeight: 1.6, color: "#83958F", margin: "14px 0 0", textAlign: "center" }}>
            Entiendo que esta constancia registra mi decisión de hoy. No impide que solicite la vacunación más adelante ni reemplaza las acciones de seguimiento y protección que correspondan al personal de salud.
          </p>
        </Seccion>

        <Seccion numero={6} titulo="Constancia y firmas">
          <Campo label="Profesional o vacunador que informó">
            <Form.Control value={f.profesional_nombre} onChange={e => set("profesional_nombre", e.target.value)} placeholder="Nombre completo" />
          </Campo>
          <Campo label="Cargo">
            <Form.Control value={f.profesional_cargo} onChange={e => set("profesional_cargo", e.target.value)} placeholder="Ej: Enfermera vacunadora" />
          </Campo>

          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#4C6360", textAlign: "center", margin: "22px 0 8px" }}>
            Firma de la madre, el padre o representante — firme con el dedo dentro del recuadro
          </label>
          {/* El canvas se oculta (no se desmonta) cuando no firma, para no perder su inicialización */}
          <div style={{ display: f.no_firma ? "none" : "block" }}>
            <div style={{ border: "1.5px solid #DDE5E0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
              <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: 160, touchAction: "none" }}
                onMouseDown={empezarFirma} onMouseMove={moverFirma} onMouseUp={terminarFirma} onMouseLeave={terminarFirma}
                onTouchStart={empezarFirma} onTouchMove={moverFirma} onTouchEnd={terminarFirma} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px 0" }}>
              <span style={{ fontSize: 12, color: "#83958F" }}>{hayFirma ? "Firma registrada" : "Aún no ha firmado"}</span>
              <button type="button" onClick={limpiarFirma} style={{ fontSize: 12.5, fontWeight: 700, color: "#CC5A26", background: "none", border: "none", cursor: "pointer" }}>Borrar firma</button>
            </div>
          </div>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 14, cursor: "pointer" }}>
            <input type="checkbox" checked={f.no_firma} onChange={e => alternarNoFirma(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2, accentColor: "#2BA8C4", flexShrink: 0 }} />
            <span style={{ fontSize: 13, lineHeight: 1.5 }}>La madre, el padre o representante no firma</span>
          </label>

          {f.no_firma && (
            <div style={{ marginTop: 12, padding: "4px 14px 14px", border: "1.5px dashed #DDE5E0", borderRadius: 14 }}>
              <Campo label="¿Se ofreció lectura y firma del formato?">
                <div style={{ display: "flex", gap: 8 }}>
                  {["Si", "No"].map(v => (
                    <button key={v} type="button" onClick={() => set("se_ofrecio_lectura_firma", v)}
                      style={{
                        flex: 1, padding: 11, borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer",
                        border: `1.5px solid ${f.se_ofrecio_lectura_firma === v ? "#0E4F57" : "#DDE5E0"}`,
                        background: f.se_ofrecio_lectura_firma === v ? "#0E4F57" : "#fff",
                        color: f.se_ofrecio_lectura_firma === v ? "#fff" : "#4C6360",
                      }}>
                      {v === "Si" ? "Sí" : "No"}
                    </button>
                  ))}
                </div>
              </Campo>
              <Campo label="Testigo">
                <Form.Control value={f.testigo_nombre} onChange={e => set("testigo_nombre", e.target.value)} placeholder="Nombre completo del testigo" />
              </Campo>
            </div>
          )}
        </Seccion>

        <Button onClick={enviar} disabled={enviando}
          style={{ width: "100%", padding: 15, borderRadius: 14, border: "none", background: "#E8703A", fontSize: 15.5, fontWeight: 700, boxShadow: "0 4px 14px rgba(232,112,58,0.35)" }}>
          {enviando ? "Enviando..." : "Registrar constancia"}
        </Button>
        <p style={{ fontSize: 11.5, color: "#83958F", textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
          Al registrar, se genera una ficha en PDF. Si se dejó un correo, se envía una copia automáticamente.
        </p>

        <p style={{ fontSize: 11, color: "#A9B6B1", textAlign: "justify", marginTop: 26, lineHeight: 1.55 }}>
          <strong>Fundamento normativo.</strong> Constitución Política, artículo 44 (derechos prevalentes de niños y niñas); Ley 1098 de 2006, artículos 8, 9, 29 y 39 (interés superior, prevalencia, esquema de vacunación en primera infancia y deber de cuidado familiar); Ley 1751 de 2015, artículo 10, literal d (información clara para decisiones libres e informadas); Ley 2406 de 2024, artículo 2, numeral 1 (acceso a vacunas y decisión libre e informada).
        </p>

        <div style={{ textAlign: "center", marginTop: 24, paddingTop: 18, borderTop: "1px solid #DDE5E0" }}>
          <a href="/consentimiento_admin" style={{ fontSize: 12, color: "#83958F", textDecoration: "none" }}>
            Panel administrativo →
          </a>
        </div>
      </div>
    </div>
  );
}

function Seccion({ numero, titulo, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 20, padding: "26px 22px", marginBottom: 16, boxShadow: "0 2px 16px rgba(14,79,87,0.07)" }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: "#2BA8C4", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 18, textAlign: "center" }}>
        <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#0E4F57", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 800, flexShrink: 0 }}>{numero}</span>
        {titulo}
      </p>
      {children}
    </div>
  );
}
function Campo({ label, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#4C6360", margin: "14px 0 6px" }}>{label}</label>
      {children}
    </div>
  );
}
function FilaResumen({ label, valor, ultima }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: ultima ? "none" : "1px solid #DDE5E0" }}>
      <span style={{ color: "#83958F" }}>{label}</span><span style={{ fontWeight: 600, textAlign: "right" }}>{valor}</span>
    </div>
  );
}